// transform-edit.ts — I1 H2: rotation / uniform scale / axis resize of a floor object, decided in the browser (lane L2).
//
// Pure port of options/api/ft3d_inventory_rotation_inc.php's decision half: the next transform_3d, the geometry size
// band a resize lands in (ft_inventory_geometry_size), the vitality it carries (ft_inventory_size_band_updates +
// ft_inventory_resize_vitality), and where the object comes to rest once turned or resized (the gravity re-check —
// turning or shrinking an object away from its wall or ceiling removes the attachment that held it). PHP persists the
// answer (inventory_floor_move_truth_commit_inc.php, gesture 'transform') under the row-state CAS; it does not re-decide.
// Oracle: tools/probes/i1_rest_z_oracle.php (transform_cases) → tools/test/janus-i1-floor-move.test.mjs.
import { inventoryRestZ, normalizeTransform, round3, type RestMover, type RestReason, type RestWorld } from './floor-move.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** SizeType::LIST (id · base_size ft · vitality_scale), tiny → titanic. Parity: the oracle's `size_ladder`. */
export const SIZE_LADDER: ReadonlyArray<{ id: string; variable: string; baseSize: number; vitalityScale: number }> = [
  { id: '13456FEC-A2EE-DB46-5829-0EB2F627E1B7', variable: 'tiny', baseSize: 2.5, vitalityScale: 0.25 },
  { id: 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7', variable: 'small', baseSize: 3.75, vitalityScale: 0.60 },
  { id: 'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC', variable: 'medium', baseSize: 5, vitalityScale: 1.00 },
  { id: '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD', variable: 'large', baseSize: 10, vitalityScale: 4.00 },
  { id: 'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C', variable: 'huge', baseSize: 15, vitalityScale: 15.0 },
  { id: 'CEA2946E-D395-46EA-A6E7-74D21EA2376E', variable: 'gargantuan', baseSize: 20, vitalityScale: 50.0 },
  { id: 'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68', variable: 'colossal', baseSize: 25, vitalityScale: 160.0 },
  { id: '4C33FD22-5382-15DA-1C46-F57F14378AFD', variable: 'titanic', baseSize: 30, vitalityScale: 500.0 },
];
const TITANIC = SIZE_LADDER[SIZE_LADDER.length - 1]!.id;

/** SizeType::getVitalityScale(): by exact id or variable name, 1.0 when unknown. */
export function vitalityScaleOf(size: string | null | undefined): number {
  const entry = SIZE_LADDER.find(e => e.id === size) ?? SIZE_LADDER.find(e => e.variable === size);
  return entry ? entry.vitalityScale : 1.0;
}

/** ft_inventory_geometry_size(): the longest local extent after scale picks the band; yaw never inflates it. */
export function geometrySizeId(boundsM: readonly number[], scale: number): string | null {
  if (boundsM.length !== 3 || !Number.isFinite(scale) || scale < 0.05 || scale > 24) return null;
  if (!boundsM.every(axis => Number.isFinite(axis) && axis > 0)) return null;
  const extentFt = Math.max(...boundsM) * scale / 0.3048;
  for (const entry of SIZE_LADDER) if (extentFt <= entry.baseSize + 0.00001) return entry.id;
  return TITANIC;
}

/** ft_inventory_resize_vitality(): keep the remaining fraction; a destroyed object stays destroyed. */
export function resizeVitality(oldMax: number, oldCurrent: number, newMax: number): { vMax: number; vCurrent: number } {
  const vCurrent = oldMax > 0
    ? (oldCurrent <= 0 ? 0 : Math.min(newMax, Math.max(1, Math.floor(newMax * oldCurrent / oldMax + 1e-9))))
    : newMax;
  return { vMax: newMax, vCurrent };
}

/** PHP round() (half away from zero) to an integer. */
const phpRound = (n: number): number => Math.sign(n) * Math.round(Math.abs(n));

/** ft_inventory_size_band_updates(): the size column, and the vitality scaled from the TEMPLATE's baseline. */
export function sizeBandUpdates(row: { vMax: number | null; vCurrent: number | null }, sizeId: string,
  template: { sizeId: string | null; vMax: number | null }): { inventorySize: string; vMax?: number; vCurrent?: number } {
  const out: { inventorySize: string; vMax?: number; vCurrent?: number } = { inventorySize: sizeId };
  const templateMax = Number(template.vMax ?? 0);
  if (templateMax > 0) {
    const newMax = Math.max(1, phpRound(templateMax * vitalityScaleOf(sizeId) / vitalityScaleOf(template.sizeId)));
    const oldMax = Number(row.vMax ?? 0);
    const oldCurrent = oldMax > 0 ? Number(row.vCurrent ?? oldMax) : templateMax;
    Object.assign(out, resizeVitality(oldMax > 0 ? oldMax : templateMax, oldCurrent, newMax));
  }
  return out;
}

export interface TransformEdit { rotationDegrees?: number; scale?: number; scaleXYZ?: number[] }

/** The transform the endpoint builds: the current one with this edit's fields replaced (renderMode, rubbleSource and
 * the pin ride along untouched). A resize records scaleMode=absolute. Encoding to the stored form is PHP's job. */
export function nextTransform(current: unknown, edit: TransformEdit): Row {
  const raw = typeof current === 'string' ? (() => { try { return JSON.parse(current) as unknown; } catch { return null; } })() : current;
  // PHP starts from the NORMALIZED current record; normalizeTransform() does not return the two carry-only fields.
  const base = normalizeTransform(raw);
  const next: Row = { ...(base ?? { schema: 'ft.inventory-transform-3d.v1' }) };
  if (base) {
    const r = asRow(raw)!;
    if (r.renderMode === 'standee') next.renderMode = 'standee';
    if (asRow(r.rubbleSource)) next.rubbleSource = JSON.parse(JSON.stringify(r.rubbleSource));
  }
  if (edit.rotationDegrees !== undefined) next.rotationDegrees = edit.rotationDegrees;
  if (edit.scale !== undefined) next.scale = edit.scale;
  if (edit.scaleXYZ !== undefined) next.scaleXYZ = [...edit.scaleXYZ];
  if (edit.scale !== undefined || edit.scaleXYZ !== undefined) next.scaleMode = 'absolute';
  return next;
}

export interface TransformEditInput {
  current: unknown;
  edit: TransformEdit;
  /** Undo / redo: the rotation this editor last saved (the endpoint's expected_rotation_degrees). */
  expectedRotationDegrees?: number | null;
  position: { x: number; y: number; z: number };
  vitality: { vMax: number | null; vCurrent: number | null };
  /** The placement's mesh id (the endpoint's posted mesh_id) and the world look the page renders for this row. */
  expectedMeshId: string | null;
  worldMesh: { id: string; boundsM: [number, number, number] | null } | null;
  template: { sizeId: string | null; vMax: number | null };
  /** The page cannot see the live wall overlays (openings / breaches): an answer that read a wall ray hands back. */
  shapeOverlayUnknown?: boolean;
}
export type TransformEditVerdict =
  | { verdict: 'commit'; transform: Row; inventorySize?: string; vMax?: number; vCurrent?: number;
      after: { x: number; y: number; z: number }; rest: RestReason; fallFt: number; landing: number | null }
  | { verdict: 'refuse'; error: string }
  | { verdict: 'handBack'; reason: 'mesh-bounds-unavailable' | 'shape-overlay' };

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * The endpoint's decision, in order: shape checks → expected rotation → the absolute-scale rule for an axis resize →
 * next transform → size band + vitality (resize) → gravity re-check at the CURRENT spot with the new footprint.
 * `moverFor` builds the mover from a (transform, size) pair — the footprint/height depend on both.
 */
export function resolveTransformEdit(input: TransformEditInput, world: RestWorld,
  moverFor: (transform: Row, inventorySize: string | null) => RestMover): TransformEditVerdict {
  const { edit } = input;
  const hasRotation = edit.rotationDegrees !== undefined, hasScale = edit.scale !== undefined, hasAxes = edit.scaleXYZ !== undefined;
  if (!hasRotation && !hasScale && !hasAxes) return { verdict: 'refuse', error: 'valid ids and at least one finite transform field are required' };
  if ((hasRotation && !finite(edit.rotationDegrees)) || (hasScale && (!finite(edit.scale) || edit.scale! < 0.05 || edit.scale! > 24))
    || (hasAxes && !(Array.isArray(edit.scaleXYZ) && edit.scaleXYZ.length === 3 && edit.scaleXYZ.every(n => finite(n) && n >= 0.05 && n <= 24)))) {
    return { verdict: 'refuse', error: 'valid ids and at least one finite transform field are required' };
  }
  const current = normalizeTransform(input.current);
  if (input.expectedRotationDegrees !== undefined && input.expectedRotationDegrees !== null
    && (!finite(input.expectedRotationDegrees) || Math.abs((current?.rotationDegrees ?? 0) - input.expectedRotationDegrees) > 0.0001)) {
    return { verdict: 'refuse', error: 'inventory_rotation_changed_since_edit' };
  }
  if (hasAxes && !hasScale && current?.scaleMode !== 'absolute') return { verdict: 'refuse', error: 'axis_resize_requires_absolute_scale' };
  const next = nextTransform(input.current, edit);
  const out: { inventorySize?: string; vMax?: number; vCurrent?: number } = {};
  if (hasScale || hasAxes) {
    const mesh = input.worldMesh;
    if (!mesh || !input.expectedMeshId || mesh.id.toUpperCase() !== input.expectedMeshId.toUpperCase() || !mesh.boundsM) {
      return { verdict: 'handBack', reason: 'mesh-bounds-unavailable' };
    }
    const axes = normalizeTransform(next)?.scaleXYZ ?? [1, 1, 1];
    const sizeId = geometrySizeId(mesh.boundsM.map((b, k) => b * axes[k]!), Number(next.scale ?? 1));
    if (!sizeId) return { verdict: 'refuse', error: 'invalid_mesh_resize_geometry' };
    Object.assign(out, sizeBandUpdates(input.vitality, sizeId, input.template));
  }
  const normalized = normalizeTransform(next);
  const mover = moverFor(next, out.inventorySize ?? null);
  const { x, y, z } = input.position;
  const rest = inventoryRestZ(world, { ...mover, mounted: normalized?.mounted === true }, x, y, z, true);
  if (input.shapeOverlayUnknown && rest.wallRayConsulted) return { verdict: 'handBack', reason: 'shape-overlay' };
  const fallFt = rest.rest === 'fell' ? Math.max(0, z - rest.z) : 0;
  const restZ = Math.abs(rest.z - z) > 0.0001 ? rest.z : z;
  return { verdict: 'commit', transform: next, ...out, after: { x, y, z: round3(restZ) }, rest: rest.rest, fallFt: round3(fallFt), landing: rest.landing };
}
